import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCw, Search, FileSpreadsheet, LogOut, Printer, AlertTriangle, X, History, Users, FileText, ShieldAlert } from "lucide-react";
import api, { clearSession, API, getToken } from "@/lib/api";
import { toast } from "sonner";
import { WaiverSettingsPanel, WaiverRecordsPanel } from "@/components/AdminWaiverPanel";

const TABS = [
  { key: "active", label: "Currently Signed In" },
  { key: "today", label: "Today" },
  { key: "all", label: "All Visitors" },
  { key: "offender", label: "Offender Checks" },
  { key: "waiver-settings", label: "Waiver Settings" },
  { key: "waiver-records", label: "Waiver Records" },
];

function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}
function fmtTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); } catch { return iso; }
}
function todayISO() { return new Date().toISOString().slice(0, 10); }

export default function AdminVisitors() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("active");
  const [signins, setSignins] = useState([]);
  const [allVisitors, setAllVisitors] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [printer, setPrinter] = useState(null);
  const [detail, setDetail] = useState(null); // { visitor, signins } modal

  const [offenderItems, setOffenderItems] = useState([]);
  const [offenderDate, setOffenderDate] = useState(todayISO());
  const [offenderDetail, setOffenderDetail] = useState(null);   // full record on row click
  const [offenderDetailLoading, setOffenderDetailLoading] = useState(false);
  const [offenderActionReason, setOffenderActionReason] = useState("");
  const [offenderActionBusy, setOffenderActionBusy] = useState(false);

  useEffect(() => { if (!getToken()) navigate("/"); }, [navigate]);

  const loadPrinter = useCallback(async () => {
    try {
      const r = await api.get("/admin/visitors/printer-status");
      setPrinter(r.data);
    } catch { /* ignore */ }
  }, []);

  const loadSignins = useCallback(async (which) => {
    setRefreshing(true);
    try {
      const params = { status: which === "active" ? "active" : "all", from: todayISO(), to: todayISO() };
      const r = await api.get("/admin/visitors", { params });
      setSignins(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load visitors");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const loadAll = useCallback(async (q) => {
    setRefreshing(true);
    try {
      const r = await api.get("/admin/visitors/list", { params: q ? { search: q } : {} });
      setAllVisitors(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load visitors");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const loadOffender = useCallback(async (d) => {
    setRefreshing(true);
    try {
      const r = await api.get("/admin/offender-checks", { params: { date: d } });
      setOffenderItems(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load offender checks");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const openOffenderDetail = async (checkId) => {
    setOffenderDetail({ check_id: checkId, loading: true });
    setOffenderActionReason("");
    setOffenderDetailLoading(true);
    try {
      const r = await api.get(`/admin/offender-checks/${checkId}`);
      setOffenderDetail({ ...r.data, check_id: checkId });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load record");
      setOffenderDetail(null);
    } finally { setOffenderDetailLoading(false); }
  };

  const offenderAction = async (decision) => {
    if (!offenderDetail?.check_id) return;
    if (decision === "approve" && !offenderActionReason.trim()) {
      toast.error("Reason is required to approve a flagged check-in.");
      return;
    }
    setOffenderActionBusy(true);
    try {
      const path = decision === "approve" ? "approve" : "deny";
      await api.post(`/admin/offender-checks/${offenderDetail.check_id}/${path}`, {
        reason: offenderActionReason.trim() || (decision === "deny" ? "denied" : ""),
      });
      toast.success(decision === "approve" ? "Approved & logged" : "Denied & logged");
      setOffenderDetail(null);
      loadOffender(offenderDate);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setOffenderActionBusy(false); }
  };

  useEffect(() => { loadPrinter(); }, [loadPrinter]);

  useEffect(() => {
    if (tab === "all") {
      loadAll(search);
    } else if (tab === "offender") {
      loadOffender(offenderDate);
    } else if (tab === "waiver-settings" || tab === "waiver-records") {
      setLoading(false);
    } else {
      loadSignins(tab);
      const id = setInterval(() => loadSignins(tab), 30_000);
      return () => clearInterval(id);
    }
    return undefined;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, offenderDate]);

  const openDetail = async (visitor_id) => {
    try {
      const r = await api.get(`/admin/visitor/${visitor_id}`);
      setDetail(r.data);
    } catch (e) {
      toast.error("Failed to load visitor");
    }
  };

  const signOutVisitor = async (visitor_id) => {
    try {
      const r = await api.post(`/admin/visitor/${visitor_id}/sign-out-active`);
      if (r.data.ok) {
        toast.success(`${r.data.name} signed out`);
      } else {
        toast.message(r.data.message || "No active sign-in.");
      }
      if (tab !== "all") loadSignins(tab);
      if (detail) openDetail(detail.visitor_id);
    } catch (e) {
      toast.error("Sign-out failed");
    }
  };

  // Print a visitor badge from THIS device (the reception PC, typically).
  // Fetches the 62×100mm PDF and opens it in a new browser tab — the PC's
  // native PDF viewer respects the embedded /MediaBox so the badge prints
  // at full 62×100mm (not cropped to letter paper like a hidden iframe).
  // The user gets the standard PC print dialog with their Brother QL-810W
  // already in the printer list.
  const reprint = async (signin_id) => {
    try {
      const token = getToken();
      const r = await fetch(
        `${API}/admin/visitors/${encodeURIComponent(signin_id)}/reprint-label-pdf`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const win = window.open(url, "_blank");
      if (!win) {
        toast.error("Pop-up blocked — allow pop-ups for this site, then try again.");
        URL.revokeObjectURL(url);
        return;
      }
      // Auto-trigger the PC print dialog once the PDF viewer has loaded.
      const tryPrint = () => { try { win.focus(); win.print(); } catch {} };
      setTimeout(tryPrint, 800);
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      toast.success("Badge opened — pick the Brother and hit Print.");
    } catch (e) {
      toast.error(`Reprint failed: ${e.message || e}`);
    }
  };

  const downloadXlsx = async () => {
    try {
      const token = getToken();
      const url = `${API}/admin/visitors/export.xlsx`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `rrdc-visitors-${todayISO()}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const logout = () => { clearSession(); navigate("/"); };

  const printerBanner = printer && !printer.configured ? (
    <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 mb-4 flex items-start gap-3" data-testid="printer-warning-banner">
      <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
      <div className="text-sm text-amber-900">
        <strong>Printer not configured.</strong> Visitors can still sign in and we'll keep records, but no labels will print. Set the IP in <button onClick={() => navigate("/settings")} className="underline font-semibold">Settings → Visitor Badge Printer</button>.
      </div>
    </div>
  ) : null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-visitors-page">
      <div className="bg-white border-b border-slate-200 px-3 sm:px-4 py-2 sm:py-3 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex items-center gap-2 sm:gap-3 flex-wrap">
          <button onClick={() => navigate("/dashboard")} className="p-2 rounded-lg hover:bg-slate-100 shrink-0" data-testid="visitors-back-btn">
            <ArrowLeft className="w-5 h-5 text-slate-700" />
          </button>
          <Users className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600 shrink-0" />
          <div className="min-w-0">
            <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.16em] sm:tracking-[0.18em] text-slate-500 font-bold">Admin</div>
            <h1 className="text-base sm:text-lg font-display font-bold text-slate-900">Visitors</h1>
          </div>
          <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
            <button onClick={() => (tab === "all" ? loadAll(search) : loadSignins(tab))} className="btn-touch inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 rounded-xl bg-slate-100 hover:bg-slate-200 font-semibold" data-testid="visitors-refresh-btn">
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} /> <span className="hidden sm:inline">Refresh</span>
            </button>
            <button onClick={downloadXlsx} className="btn-touch inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold" data-testid="visitors-export-btn">
              <FileSpreadsheet className="w-4 h-4" /> <span className="hidden sm:inline">Excel</span>
            </button>
            <button onClick={logout} className="btn-touch inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 rounded-xl bg-slate-900 text-white font-semibold" data-testid="visitors-logout-btn">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Lock</span>
            </button>
          </div>
        </div>
        {/* Tabs */}
        <div className="max-w-7xl mx-auto mt-3 flex gap-1 overflow-x-auto no-scrollbar">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
                    className={`shrink-0 px-3 py-1.5 rounded-lg text-sm font-semibold uppercase tracking-[0.1em] transition whitespace-nowrap ${
                      tab === t.key ? "bg-amber-500 text-white shadow" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                    data-testid={`visitors-tab-${t.key}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4">
        {printerBanner}

        {tab === "all" && (
          <form onSubmit={(e) => { e.preventDefault(); loadAll(search); }} className="mb-4 flex gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or phone…"
                     className="w-full pl-10 pr-3 py-2 rounded-xl border border-slate-200 outline-none focus:ring-2 focus:ring-amber-400"
                     data-testid="visitors-search-input" />
            </div>
            <button type="submit" className="px-4 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-semibold" data-testid="visitors-search-btn">
              Search
            </button>
          </form>
        )}

        {loading && <div className="text-slate-500 text-sm">Loading…</div>}

        {!loading && tab !== "all" && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="visitors-card-grid">
            {signins.length === 0 && (
              <div className="text-slate-500 col-span-full text-center py-12">
                {tab === "active" ? "No visitors are currently signed in." : "No visitor sign-ins for today."}
              </div>
            )}
            {signins.map((v) => {
              const isActive = !v.signed_out_at;
              return (
                <div key={v.signin_id} className="bg-white rounded-2xl border border-slate-200 p-4 hover:shadow-md transition" data-testid={`visitor-card-${v.signin_id}`}>
                  <div className="flex gap-3">
                    {v.photo_base64 ? (
                      <img src={`data:image/jpeg;base64,${v.photo_base64}`} alt={v.name} className="w-16 h-16 rounded-xl object-cover border border-slate-200" />
                    ) : (
                      <div className="w-16 h-16 rounded-xl bg-slate-100 flex items-center justify-center text-slate-400">
                        <Users className="w-7 h-7" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <button onClick={() => openDetail(v.visitor_id)} className="text-left">
                        <div className="font-bold text-slate-900 truncate hover:underline" data-testid={`visitor-name-${v.signin_id}`}>{v.name}</div>
                      </button>
                      <div className="text-xs text-slate-500">····{(v.phone || "").slice(-4)} · #{v.visit_count} visit{v.visit_count === 1 ? "" : "s"}</div>
                      <div className="text-xs text-slate-500 mt-1">
                        In {fmtTime(v.signed_in_at)}
                        {v.signed_out_at && <> · Out {fmtTime(v.signed_out_at)} <span className="uppercase tracking-wide text-[10px] text-slate-400">({v.sign_out_method})</span></>}
                      </div>
                      {v.is_first_visit && <span className="inline-block mt-1 text-[10px] uppercase tracking-wide font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">First visit</span>}
                      {v.needs_photo && <span className="inline-block mt-1 ml-1 text-[10px] uppercase tracking-wide font-bold text-amber-800 bg-amber-50 border border-amber-300 px-1.5 py-0.5 rounded" data-testid={`visitor-needs-photo-${v.signin_id}`}>Needs photo</span>}
                      {v.label_print_error && printer?.print_mode !== "airprint" && (
                        <div className="text-[11px] text-red-700 mt-1 flex items-center gap-1" title={v.label_print_error}>
                          <AlertTriangle className="w-3 h-3" /> Print failed
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 flex gap-2">
                    {isActive && (
                      <button onClick={() => signOutVisitor(v.visitor_id)}
                              className="flex-1 text-sm rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold py-2 inline-flex items-center justify-center gap-1"
                              data-testid={`visitor-signout-btn-${v.signin_id}`}>
                        <LogOut className="w-4 h-4" /> Sign Out
                      </button>
                    )}
                    {(v.label_print_error || !v.label_printed) && (
                      <button onClick={() => reprint(v.signin_id)}
                              className="flex-1 text-sm rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-900 font-semibold py-2 inline-flex items-center justify-center gap-1"
                              data-testid={`visitor-reprint-btn-${v.signin_id}`}>
                        <Printer className="w-4 h-4" /> Reprint Label
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && tab === "all" && (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="visitors-all-list">
            {allVisitors.length === 0 ? (
              <div className="text-slate-500 text-center py-12">No visitors match.</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {allVisitors.map((v) => (
                  <li key={v.visitor_id}>
                    <button onClick={() => openDetail(v.visitor_id)} className="w-full text-left p-4 hover:bg-slate-50 flex items-center gap-3" data-testid={`visitor-row-${v.visitor_id}`}>
                      {v.photo_base64 ? (
                        <img src={`data:image/jpeg;base64,${v.photo_base64}`} alt={v.name} className="w-12 h-12 rounded-xl object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center text-slate-400"><Users className="w-5 h-5" /></div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-900">{v.name}</div>
                        <div className="text-xs text-slate-500">{v.phone_display || v.phone} · {v.visit_count} visit{v.visit_count === 1 ? "" : "s"} · last {fmtDateTime(v.last_visit_at)}</div>
                      </div>
                      <History className="w-4 h-4 text-slate-400" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {tab === "offender" && (
          <section data-testid="admin-offender-checks">
            <div className="flex items-center gap-2 mb-3">
              <ShieldAlert className="w-5 h-5 text-red-600" />
              <h2 className="font-bold text-slate-900">Sex-Offender Registry Checks</h2>
              <input type="date" value={offenderDate}
                     onChange={(e) => setOffenderDate(e.target.value)}
                     className="ml-auto px-3 py-1.5 rounded-xl border border-slate-200 outline-none focus:ring-2 focus:ring-amber-400"
                     data-testid="offender-date-picker" />
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              {offenderItems.length === 0 ? (
                <div className="text-slate-500 text-center py-12">No checks recorded for {offenderDate}.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="text-left px-3 py-2">Time</th>
                      <th className="text-left px-3 py-2">Visitor Name</th>
                      <th className="text-left px-3 py-2">Phone</th>
                      <th className="text-left px-3 py-2">Status</th>
                      <th className="text-right px-3 py-2">Hits</th>
                      <th className="text-left px-3 py-2">Override By / Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {offenderItems.map((c) => (
                      <tr key={c.check_id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" data-testid={`offender-row-${c.check_id}`} onClick={() => openOffenderDetail(c.check_id)}>
                        <td className="px-3 py-2 whitespace-nowrap">{fmtTime(c.queried_at_utc)}</td>
                        <td className="px-3 py-2 font-semibold text-slate-900">{c.name}</td>
                        <td className="px-3 py-2 text-slate-700">····{(c.phone || "").slice(-4)}</td>
                        <td className="px-3 py-2">
                          {c.status === "cleared" && <span className="text-xs font-bold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded">Cleared</span>}
                          {c.status === "flagged" && <span className="text-xs font-bold uppercase tracking-wide text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded">Flagged</span>}
                          {c.status === "admin_override" && <span className="text-xs font-bold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">Override-Approved</span>}
                          {c.status === "admin_denied" && <span className="text-xs font-bold uppercase tracking-wide text-slate-700 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded">Denied</span>}
                          {c.status === "not_configured" && <span className="text-xs font-bold uppercase tracking-wide text-slate-500 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded">No-API</span>}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{c.hit_count}</td>
                        <td className="px-3 py-2 text-xs text-slate-600">{c.override_by ? `${c.override_by} — ${c.override_reason || ""}` : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        )}

        {tab === "waiver-settings" && <WaiverSettingsPanel />}
        {tab === "waiver-records" && <WaiverRecordsPanel />}
      </div>

      {/* Offender check detail modal */}
      {offenderDetail && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setOffenderDetail(null)} data-testid="offender-detail-modal">
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between sticky top-0 bg-white">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-red-700 font-bold">Offender Registry — Match Record</div>
                <h3 className="text-lg font-bold text-slate-900">{offenderDetail.name || "—"}</h3>
                <div className="text-xs text-slate-500">{offenderDetail.queried_at_utc ? new Date(offenderDetail.queried_at_utc).toLocaleString() : ""} · phone ····{(offenderDetail.phone || "").slice(-4)}</div>
              </div>
              <button onClick={() => setOffenderDetail(null)} className="p-1 rounded hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              {offenderDetailLoading || offenderDetail.loading ? (
                <div className="text-slate-500 text-sm">Loading…</div>
              ) : (
                <>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-slate-500 uppercase tracking-[0.16em] font-bold">Status:</span>
                    {offenderDetail.status === "cleared" && <span className="px-2 py-0.5 rounded font-bold uppercase bg-emerald-50 text-emerald-700 border border-emerald-200">Cleared</span>}
                    {offenderDetail.status === "flagged" && <span className="px-2 py-0.5 rounded font-bold uppercase bg-red-50 text-red-700 border border-red-200">Flagged · {offenderDetail.hit_count} hit{offenderDetail.hit_count === 1 ? "" : "s"}</span>}
                    {offenderDetail.status === "admin_override" && <span className="px-2 py-0.5 rounded font-bold uppercase bg-amber-50 text-amber-700 border border-amber-200">Override-Approved</span>}
                    {offenderDetail.status === "admin_denied" && <span className="px-2 py-0.5 rounded font-bold uppercase bg-slate-100 text-slate-700 border border-slate-200">Denied</span>}
                    {offenderDetail.status === "not_configured" && <span className="px-2 py-0.5 rounded font-bold uppercase bg-slate-50 text-slate-500 border border-slate-200">No-API</span>}
                  </div>

                  {offenderDetail.override_by && (
                    <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm">
                      <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-bold mb-1">Decision</div>
                      <div className="text-slate-900"><strong>{offenderDetail.override_by}</strong> · {offenderDetail.override_reason || "—"}</div>
                      {offenderDetail.override_at_utc && <div className="text-[11px] text-slate-500 mt-1">{new Date(offenderDetail.override_at_utc).toLocaleString()}</div>}
                    </div>
                  )}

                  <div>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-bold mb-2">Registry Hits ({(offenderDetail.hits || []).length})</div>
                    <div className="space-y-2 max-h-80 overflow-auto">
                      {(offenderDetail.hits || []).length === 0 ? (
                        <div className="text-sm text-slate-500 italic">No hits recorded.</div>
                      ) : (offenderDetail.hits || []).map((h, i) => (
                        <div key={i} className="rounded-lg border border-red-100 bg-red-50/40 p-3 flex gap-3" data-testid={`offender-detail-hit-${i}`}>
                          {h.offenderImageUrl ? (
                            <img src={h.offenderImageUrl} alt="" className="w-16 h-16 rounded object-cover bg-slate-100 flex-shrink-0" />
                          ) : (
                            <div className="w-16 h-16 rounded bg-slate-100 flex-shrink-0" />
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="font-bold text-slate-900">{h.name || `${h.firstName || ""} ${h.lastName || ""}`}</div>
                            <div className="text-xs text-slate-700">{[h.city, h.state, h.zipcode].filter(Boolean).join(", ")}</div>
                            {h.dob && <div className="text-xs text-slate-500">DOB: {String(h.dob).slice(0, 10)}</div>}
                            {h.crime && <div className="text-xs text-slate-600 mt-1 line-clamp-3">{h.crime}</div>}
                            {h.offenderUrl && <a href={h.offenderUrl} target="_blank" rel="noreferrer" className="text-xs text-red-700 underline">State registry record →</a>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {(offenderDetail.status === "flagged" || offenderDetail.status === "admin_override") && (
                    <div className="pt-4 border-t border-slate-100 space-y-2">
                      <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-bold">Director Decision</div>
                      <textarea value={offenderActionReason} onChange={(e) => setOffenderActionReason(e.target.value)}
                                rows={2} maxLength={500}
                                placeholder="Reason / decision notes (required to approve)"
                                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-amber-400 outline-none"
                                data-testid="offender-action-reason" />
                      <div className="grid grid-cols-2 gap-2">
                        <button onClick={() => offenderAction("deny")}
                                disabled={offenderActionBusy}
                                className="rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold py-3 disabled:opacity-50"
                                data-testid="offender-action-deny">
                          Deny
                        </button>
                        <button onClick={() => offenderAction("approve")}
                                disabled={offenderActionBusy || !offenderActionReason.trim()}
                                className="rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold py-3 disabled:opacity-50"
                                data-testid="offender-action-approve">
                          {offenderActionBusy ? "Working…" : "Approve"}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {detail && (
        <VisitorDetailModal
          data={detail}
          onClose={() => setDetail(null)}
          onSignOut={() => signOutVisitor(detail.visitor_id)}
          onReprint={(sid) => reprint(sid)}
          onPhotoUpdated={async () => openDetail(detail.visitor_id)}
        />
      )}
    </div>
  );
}

function VisitorDetailModal({ data, onClose, onSignOut, onReprint, onPhotoUpdated }) {
  const v = data.visitor;
  const signins = data.signins || [];
  const hasActive = signins.some((s) => !s.signed_out_at);
  const fileRef = useRef(null);

  const onUploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post(`/admin/visitor/${data.visitor_id}/update-photo`, { photo_base64: String(reader.result) });
        toast.success("Photo updated");
        onPhotoUpdated();
      } catch {
        toast.error("Photo update failed");
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="visitor-detail-modal">
      <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <h3 className="text-lg font-bold text-slate-900">Visitor Detail</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="flex gap-4">
            {v.photo_base64 ? (
              <img src={`data:image/jpeg;base64,${v.photo_base64}`} alt={v.name} className="w-32 h-32 rounded-2xl object-cover border border-slate-200" />
            ) : (
              <div className="w-32 h-32 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-400"><Users className="w-10 h-10" /></div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-2xl font-bold text-slate-900">{v.name}</div>
              <div className="text-sm text-slate-700">{v.phone_display || v.phone}</div>
              {(v.phone_history || []).filter((p) => p !== v.phone).length > 0 && (
                <div className="text-xs text-slate-500 mt-1">Prior: {(v.phone_history || []).filter((p) => p !== v.phone).join(", ")}</div>
              )}
              <div className="text-xs text-slate-500 mt-2">
                First visit {fmtDateTime(v.first_visit_at)} · Last {fmtDateTime(v.last_visit_at)} · {v.visit_count} total
              </div>
              <div className="mt-3 flex gap-2 flex-wrap">
                {hasActive && (
                  <button onClick={onSignOut} className="text-sm rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold px-3 py-2 inline-flex items-center gap-1" data-testid="visitor-detail-signout-btn">
                    <LogOut className="w-4 h-4" /> Sign Out Active Visit
                  </button>
                )}
                <button onClick={() => fileRef.current?.click()} className="text-sm rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-900 font-semibold px-3 py-2" data-testid="visitor-detail-update-photo-btn">
                  Update Photo
                </button>
                <input ref={fileRef} type="file" accept="image/*" capture="user" className="hidden" onChange={onUploadFile} />
              </div>
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.16em] text-slate-500 font-bold mb-2">Visit History ({signins.length})</div>
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2">Sign-In</th>
                    <th className="text-left px-3 py-2">Sign-Out</th>
                    <th className="text-left px-3 py-2">Method</th>
                    <th className="text-left px-3 py-2">Offender Check</th>
                    <th className="text-left px-3 py-2">Waiver</th>
                    <th className="text-left px-3 py-2">Label</th>
                  </tr>
                </thead>
                <tbody>
                  {signins.map((s) => {
                    const oc = s.offender_check;
                    return (
                    <tr key={s.signin_id} className="border-t border-slate-100" data-testid={`visit-row-${s.signin_id}`}>
                      <td className="px-3 py-2 align-top">{fmtDateTime(s.signed_in_at)}</td>
                      <td className="px-3 py-2 align-top">{s.signed_out_at ? fmtDateTime(s.signed_out_at) : <span className="text-emerald-700 font-semibold">Active</span>}</td>
                      <td className="px-3 py-2 align-top uppercase text-xs tracking-wide text-slate-500">{s.sign_out_method || "—"}</td>
                      <td className="px-3 py-2 align-top">
                        {!oc ? (
                          <span className="text-xs text-slate-400">—</span>
                        ) : oc.status === "cleared" ? (
                          <span className="inline-flex items-center text-xs font-bold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded" data-testid={`offender-cell-${s.signin_id}`}>Cleared</span>
                        ) : oc.status === "admin_override" ? (
                          <div data-testid={`offender-cell-${s.signin_id}`}>
                            <div className="inline-flex items-center text-xs font-bold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">Override · {oc.hit_count} hit{oc.hit_count === 1 ? "" : "s"}</div>
                            {(oc.override_by || oc.override_reason) && (
                              <div className="text-[11px] text-slate-600 mt-1 max-w-[220px]"><span className="font-semibold">{oc.override_by || "Admin"}:</span> {oc.override_reason || "—"}</div>
                            )}
                          </div>
                        ) : oc.status === "admin_denied" ? (
                          <span className="inline-flex items-center text-xs font-bold uppercase tracking-wide text-slate-700 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded" data-testid={`offender-cell-${s.signin_id}`}>Denied</span>
                        ) : oc.status === "flagged" ? (
                          <span className="inline-flex items-center text-xs font-bold uppercase tracking-wide text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded" data-testid={`offender-cell-${s.signin_id}`}>Flagged · {oc.hit_count}</span>
                        ) : oc.status === "not_configured" ? (
                          <span className="inline-flex items-center text-xs font-bold uppercase tracking-wide text-slate-500 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded" data-testid={`offender-cell-${s.signin_id}`}>No-API</span>
                        ) : (
                          <span className="text-xs text-slate-400" data-testid={`offender-cell-${s.signin_id}`}>—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top">
                        {s.waiver_signed ? (
                          <span className="text-emerald-700 text-xs font-semibold">Signed</span>
                        ) : (
                          <span className="text-slate-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top">
                        {s.label_printed ? (
                          <span className="text-emerald-700 text-xs font-semibold">Printed</span>
                        ) : s.label_print_error ? (
                          <span className="text-red-700 text-xs" title={s.label_print_error}>Failed · <button onClick={() => onReprint(s.signin_id)} className="underline">Retry</button></span>
                        ) : (
                          <button onClick={() => onReprint(s.signin_id)} className="text-amber-700 text-xs underline">Print</button>
                        )}
                      </td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
